/**
 * Step Navigation Utilities
 * Pure functions for handling step navigation logic
 */

import type { Dispatch } from '@reduxjs/toolkit';

import {
  MESSAGE_SUBTYPES,
  MESSAGE_TYPES,
  STEP_NAMES,
  TIMEOUTS,
} from '../constants/steps';
import type { CopilotState } from '../data-layer/types';
import {
  addMessage,
  addVisitedStep,
  removeMessagesBySubType,
  setActiveStep,
} from '../data-store/copilotSlice';

import { showConnectionForm, showUpdateAccountForm } from './stepFormUtils';
import { safeTimeout } from './timeoutUtils';

/**
 * Navigate to connection step with transition handling
 */
const navigateToConnection = (
  dispatch: Dispatch,
  getState: () => unknown,
  isEditMode: boolean
): void => {
  if (isEditMode) {
    return;
  }

  const state = getState() as { copilot?: CopilotState };
  const hasVisitedConnection = state.copilot?.navigation.visitedSteps.includes(
    STEP_NAMES.CONNECTION
  );

  if (hasVisitedConnection) {
    dispatch(setActiveStep(STEP_NAMES.CONNECTION));
    showConnectionForm(dispatch, getState);
  } else {
    dispatch(
      addMessage({
        id: `transition-${Date.now()}`,
        type: MESSAGE_TYPES.AI_RESPONSE,
        subType: MESSAGE_SUBTYPES.GENERAL_TO_CONNECTION,
        loader: true,
        timestamp: Date.now(),
      })
    );

    safeTimeout(() => {
      dispatch(removeMessagesBySubType(MESSAGE_SUBTYPES.GENERAL_TO_CONNECTION));
      dispatch(addVisitedStep(STEP_NAMES.CONNECTION));
      dispatch(setActiveStep(STEP_NAMES.CONNECTION));
      showConnectionForm(dispatch, getState);
    }, TIMEOUTS.TRANSITION_MESSAGE_DURATION);
  }
};

/**
 * Navigate to update account step with transition handling
 */
export const navigateToUpdateAccount = (
  dispatch: Dispatch,
  getState: () => unknown,
  isEditMode: boolean
): void => {
  if (isEditMode) {
    return;
  }

  const state = getState() as { copilot?: CopilotState };
  const hasVisitedUpdateAccount =
    state.copilot?.navigation.visitedSteps.includes(STEP_NAMES.UPDATE_ACCOUNT);

  if (hasVisitedUpdateAccount) {
    dispatch(setActiveStep(STEP_NAMES.UPDATE_ACCOUNT));
    showUpdateAccountForm(dispatch, getState);
  } else {
    dispatch(
      addMessage({
        id: `transition-${Date.now()}`,
        type: MESSAGE_TYPES.AI_RESPONSE,
        subType: MESSAGE_SUBTYPES.PROCEED_TO_UPDATE_ACCOUNT,
        loader: true,
        timestamp: Date.now(),
      })
    );

    safeTimeout(() => {
      dispatch(
        removeMessagesBySubType(MESSAGE_SUBTYPES.PROCEED_TO_UPDATE_ACCOUNT)
      );
      dispatch(addVisitedStep(STEP_NAMES.UPDATE_ACCOUNT));
      dispatch(setActiveStep(STEP_NAMES.UPDATE_ACCOUNT));
      showUpdateAccountForm(dispatch, getState);
    }, TIMEOUTS.TRANSITION_MESSAGE_DURATION);
  }
};

export default navigateToConnection;
